import { Auth0Provider, OAuthError, useAuth0, User } from '@auth0/auth0-react';
import { LoadingView } from '@payflip/ui/v2';
import { shutdownZendesk } from '@payflip/web/zendesk';
import { createContext, ReactNode, useContext } from 'react';

export type AuthConfig = {
  clientId: string;
  domain: string;
  audience: string;
  logoutUrl: string;
  isPartner: boolean;
  connection?: string;
  /** Determines the login method used by Auth0. Defaults to 'redirect' if omitted */
  loginMethod?: 'redirect' | 'popup';
  /** Determines the loading view position. Defaults to 'overlay' if omitted */
  loaderPosition?: 'overlay' | 'inline';
};

type SignOutOptions = {
  returnTo?: string;
};

export type PayflipAuth0User = User & {
  'https://sso.payflip.be/upn'?: string;
};

export type AuthContextValues = {
  getAccessToken: () => Promise<null | string>;
  signOut: (options?: SignOutOptions) => void;
  signIn: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  auth0User?: PayflipAuth0User;
};

const AuthContext = createContext<AuthContextValues>({
  getAccessToken: async () => null,
  signOut: () => null,
  signIn: () => null,
  isAuthenticated: false,
  isLoading: true
});

type AuthContextProviderProps = { children: ReactNode; config: AuthConfig };
function AuthContextProvider({
  children,
  config
}: AuthContextProviderProps): JSX.Element {
  const {
    getAccessTokenSilently,
    logout,
    user,
    isAuthenticated,
    isLoading: isLoadingAuth0,
    loginWithRedirect,
    loginWithPopup,
    error
  } = useAuth0<PayflipAuth0User>();

  const getAccessToken = async (): Promise<null | string> => {
    try {
      return isAuthenticated ? await getAccessTokenSilently() : null;
    } catch (_err) {
      const err = _err as OAuthError;
      if (err.error === 'login_required') {
        await signIn();
        return null;
      }
      if (err.error === 'consent_required') {
        await signIn();
        return null;
      }
      throw err;
    }
  };

  const signOut = (options?: SignOutOptions): void => {
    logout({
      client_id: config.clientId,
      returnTo: options?.returnTo ?? config.logoutUrl,
      federated: config.isPartner
    });
    shutdownZendesk();
  };

  const signIn = async (): Promise<void> => {
    const params = new URLSearchParams(window.location.search);
    const loginMethod = config.loginMethod ?? 'redirect';
    const loginHint = params.get('login_hint') ?? undefined;
    switch (loginMethod) {
      case 'redirect': {
        await loginWithRedirect({
          login_hint: loginHint,
          connection: config.connection
        });
        break;
      }
      case 'popup': {
        await loginWithPopup({
          login_hint: loginHint,
          connection: config.connection
        });
        break;
      }
      default:
        throw new Error(`Unknown login method ${loginMethod}`);
    }
  };

  if (isLoadingAuth0 && !isAuthenticated) {
    const loaderPosition = config.loaderPosition ?? 'overlay';
    const fixed = loaderPosition === 'overlay' ? true : false;
    return <LoadingView fixed={fixed} />;
  }

  return (
    <AuthContext.Provider
      value={{
        auth0User: user,
        getAccessToken,
        signOut,
        signIn,
        isAuthenticated: isAuthenticated && !error,
        isLoading: isLoadingAuth0
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

type AuthProviderProps = { children: ReactNode; config: AuthConfig };
export function AuthProvider({
  children,
  config
}: AuthProviderProps): JSX.Element {
  return (
    <Auth0Provider
      domain={config.domain}
      clientId={config.clientId}
      redirectUri={window.location.origin}
      audience={config.audience}
      scope="openid profile email"
      connection={config.connection}
    >
      <AuthContextProvider config={config}>{children}</AuthContextProvider>
    </Auth0Provider>
  );
}

export function useAuth(): AuthContextValues {
  if (!AuthContext) {
    throw new Error('useAuth should be called within AuthProvider context');
  }
  return useContext(AuthContext);
}
